import { useState } from "react";
import GuideShell from "@/components/GuideShell";
import { Link, useLocation } from "wouter";
import { GHL_WEBHOOKS, submitOptIn } from "@/lib/ghl";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  cream: "oklch(0.98 0.008 85)",
  linen: "oklch(0.93 0.01 85)",
  espresso: "oklch(0.22 0.015 65)",
  terracotta: "oklch(0.32 0.08 255)",
  terracottaLight: "oklch(0.32 0.08 255 / 0.08)",
  terracottaMid: "oklch(0.32 0.08 255 / 0.22)",
  border: "oklch(0.88 0.012 85)",
  white: "oklch(1 0.004 85)",
  muted: "oklch(0.52 0.015 65)",
  body: "oklch(0.52 0.015 65)",
};


export default function ClaudeQuickWinsPage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [, navigate] = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      await submitOptIn(GHL_WEBHOOKS.claudeQuickWins, {
        firstName: firstName.trim(),
        email: email.trim(),
        source: "claude-quickwins",
      });
      setStatus("success");
      setTimeout(() => navigate("/claude-quick-wins-delivery"), 2500);
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  }

  return (
    /* The opt-in for the Claude guide. Reachable from an email as easily as
       from the path page, so it carries the same way back as every other
       guide rather than relying on the browser's own button. */
    <GuideShell context="Quick Wins · Claude" backHref="/resources/claude" backLabel="Back to Claude">

      <main style={{ maxWidth: 960, margin: "0 auto", padding: "4rem 1.5rem 5rem", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4rem", alignItems: "start" }}>

        {/* ── LEFT: Copy ── */}
        <div>
          <p style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "1rem" }}>
            A free guide for people who want thinking that actually matters
          </p>
          <h1 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "clamp(28px, 4vw, 40px)", fontWeight: 700, color: C.espresso, lineHeight: 1.2, marginBottom: "1rem" }}>
            Finally.{" "}
            <em style={{ fontStyle: "italic", color: C.terracotta }}>A thinking tool that slows you down in the right way.</em>
          </h1>
          <p style={{ fontSize: 15, color: C.body, lineHeight: 1.8, marginBottom: "2rem" }}>
            Claude works best when you give it time, and take time yourself. This guide walks you through 14 Quick Wins that show you exactly how to use Claude as a deliberate thinking partner, especially when clarity, tone, and stakes are high.
          </p>

          {/* What's inside */}
          <div style={{ borderLeft: `2px solid ${C.terracotta}`, paddingLeft: "1.25rem", marginBottom: "2rem" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, color: C.espresso, marginBottom: "0.75rem" }}>
              What's inside
            </p>
            {[
              "How to use Claude for calm, thoughtful iteration instead of rapid expansion",
              "How to preserve your voice while getting structural feedback",
              "How to slow the process on purpose when decisions feel complex",
              "How to use Claude's new Projects and Customization features for deeper thinking",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: "0.75rem" }}>
                <span style={{ color: C.terracotta, fontWeight: 700, fontSize: 13, marginTop: 1, flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: 14, color: C.body, lineHeight: 1.65, margin: 0 }}>{item}</p>
              </div>
            ))}
          </div>

          {/* About: image + text */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.5rem" }}>
            <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "1rem" }}>
              About the creator
            </p>
            <div style={{ display: "flex", gap: "1.25rem", alignItems: "flex-start" }}>
              <img
                src="/assets/caren-headshot_722f0498.png"
                alt="Caren Glasser"
                style={{ width: 88, height: 88, borderRadius: "50%", objectFit: "cover", objectPosition: "top", flexShrink: 0, border: `2px solid ${C.border}` }}
              />
              <div>
                <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 18, fontWeight: 700, color: C.espresso, marginBottom: "0.2rem" }}>
                  Caren Glasser
                </p>
                <p style={{ fontSize: 12, color: C.muted, fontStyle: "italic", marginBottom: "0.65rem" }}>
                  Tech Evangelist. Turning clarity into systems.
                </p>
                <p style={{ fontSize: 13, color: C.body, lineHeight: 1.7, marginBottom: "0.65rem" }}>
                  I built the Smart Stack because I was tired of AI content that talked at people instead of with them. This guide is a calm, honest introduction to Claude that respects your intelligence and your pace.
                </p>
                <p style={{ fontSize: 11, color: C.muted }}>
                  Smart Stack ·{" "}
                  <a href="https://carenglasser.com" target="_blank" rel="noopener noreferrer" style={{ color: C.terracotta, textDecoration: "underline" }}>
                    carenglasser.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Form card (sticky) ── */}
        {/* 124px clears the sticky header: 64px for the wordmark row plus 44px
            for the back row, and a gap. It was 80, which was right when the
            header was one row and the back link scrolled away with the page. */}
        <div style={{ position: "sticky", top: 124 }}>
          {status === "success" ? (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 18, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", padding: "2.25rem 2.5rem", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: C.terracottaLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem" }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.terracotta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, fontWeight: 700, color: C.espresso, marginBottom: "0.75rem" }}>
                You're in.
              </h2>
              <p style={{ fontSize: 14, color: C.muted, lineHeight: 1.75, marginBottom: "0.5rem" }}>
                Your Claude Quick Wins guide is ready. Taking you to sign up now.
              </p>
              <p style={{ fontSize: 12, color: C.muted, marginBottom: "1.5rem" }}>
                No spam. No sequences you didn't ask for. Just the guide.
              </p>
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "1.25rem" }}>
                <Link href="/resources/claude" style={{ fontSize: 13, color: C.terracotta, fontWeight: 600, textDecoration: "none" }}>
                  Explore all Claude resources →
                </Link>
              </div>
            </div>
          ) : (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 18, boxShadow: "0 4px 24px rgba(0,0,0,0.07)", padding: "2.25rem 2.5rem" }}>
              {/* Card header */}
              <div style={{ marginBottom: "1.75rem" }}>
                <p style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, color: C.muted, marginBottom: "0.4rem" }}>
                  Free Guide
                </p>
                <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24, fontWeight: 700, color: C.espresso, lineHeight: 1.25, marginBottom: "0.4rem" }}>
                  Claude Quick Wins
                </h2>
                <p style={{ fontSize: 13, color: C.muted }}>Slow the process. Protect your voice. Let depth do its work.</p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={{ display: "block", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: C.body, marginBottom: 6 }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Your first name"
                    required
                    style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, color: C.espresso, background: C.cream, outline: "none", boxSizing: "border-box", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = C.terracotta; e.currentTarget.style.boxShadow = `0 0 0 3px oklch(0.32 0.08 255 / 0.12)`; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = "none"; }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, color: C.body, marginBottom: 6 }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    style={{ width: "100%", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 14, color: C.espresso, background: C.cream, outline: "none", boxSizing: "border-box", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = C.terracotta; e.currentTarget.style.boxShadow = `0 0 0 3px oklch(0.32 0.08 255 / 0.12)`; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = "none"; }}
                  />
                </div>

                {errorMsg && <p style={{ fontSize: 13, color: C.terracotta }}>{errorMsg}</p>}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  style={{ width: "100%", background: C.terracotta, color: C.white, border: "none", borderRadius: 8, padding: "13px 20px", fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", cursor: status === "loading" ? "not-allowed" : "pointer", opacity: status === "loading" ? 0.7 : 1, fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", marginTop: 4, transition: "background 0.2s ease" }}
                  onMouseEnter={(e) => { if (status !== "loading") e.currentTarget.style.background = "oklch(0.26 0.075 255)"; }}
                  onMouseLeave={(e) => { if (status !== "loading") e.currentTarget.style.background = C.terracotta; }}
                >
                  {status === "loading" ? "Sending…" : "Send me the free guide →"}
                </button>
              </form>

              <p style={{ fontSize: 11, color: C.muted, textAlign: "center", marginTop: 14 }}>
                No spam. No sequences you didn't ask for. Just the guide.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Responsive: collapse to single column on mobile */}
      <style>{`
        @media (max-width: 680px) {
          main { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        }
      `}</style>

    </GuideShell>
  );
}
